import {
  differenceInMinutes,
  differenceInSeconds,
  format,
  isAfter,
  isBefore,
  addSeconds,
} from 'date-fns';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import getFullNameWithTitle from '@medflex/utils/getFullNameWithTitle';
import { VideoCameraIcon } from '@heroicons/react/24/solid';
import ChatMessageBubble from '../ChatMessageBubble/ChatMessageBubble';

const SessionCallMessage = ({
  createdAt,
  endedAt,
  lastTouchedAt,
  establishedAt,
  sender,
  isSender,
  participants,
  now,
}) => {
  const { t } = useTranslation('components');
  const establishTimeString = format(establishedAt, 'HH:mm');
  const endTimeString = format(endedAt || lastTouchedAt, 'HH:mm');
  const createdTimeString = format(createdAt || lastTouchedAt, 'HH:mm');

  const minutes = differenceInMinutes(endedAt || lastTouchedAt, establishedAt);
  const seconds = differenceInSeconds(endedAt || lastTouchedAt, establishedAt) % 60;

  const started =
    !endedAt &&
    isBefore(now, addSeconds(new Date(establishedAt || lastTouchedAt || createdAt), 30));
  const ended = !!endedAt && establishedAt;
  const canceled = !!endedAt && !establishedAt;
  const missed =
    canceled ||
    (!endedAt &&
      isAfter(now, addSeconds(new Date(establishedAt || lastTouchedAt || createdAt), 30)));

  const caller = participants.find(p => p.userId === sender.id);
  const callee = participants.find(p => p !== caller);

  return (
    <ChatMessageBubble>
      <div className="flex justify-center items-center italic">
        <VideoCameraIcon className="w-8 h-8 text-slate-400 fill-current" />
        <span className="ml-4">
          {started && t('SessionCallMessage.started')}
          {missed && (
            <>
              {t('SessionCallMessage.missed.prefix', { createdAt: createdTimeString })}
              {isSender
                ? t('SessionCallMessage.missed.unreachable', {
                    callee: callee && getFullNameWithTitle({ id: callee.userId, ...callee }),
                  })
                : t('SessionCallMessage.missed.missed', {
                    caller: caller && getFullNameWithTitle({ id: caller.userId, ...caller }),
                  })}
            </>
          )}
          {ended && (
            <>
              {t('SessionCallMessage.ended.prefix', {
                startTime: establishTimeString,
                endTime: endTimeString,
              })}
              {minutes > 0
                ? t('SessionCallMessage.ended.minutes', { minutes, count: minutes })
                : ''}
              {minutes > 0 && seconds > 0 ? ` ${t('SessionCallMessage.ended.and')} ` : ''}
              {seconds > 0
                ? t('SessionCallMessage.ended.seconds', { seconds, count: seconds })
                : ''}
            </>
          )}
        </span>
      </div>
    </ChatMessageBubble>
  );
};

SessionCallMessage.defaultProps = {
  now: new Date(),
};

SessionCallMessage.propTypes = {
  createdAt: PropTypes.string,
  endedAt: PropTypes.string,
  participants: PropTypes.arrayOf(
    PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      title: PropTypes.string,
      userId: PropTypes.string,
    }),
  ),
  sender: PropTypes.shape({
    id: PropTypes.string,
  }),
  isSender: PropTypes.bool,
  establishedAt: PropTypes.string,
  lastTouchedAt: PropTypes.string,
  now: PropTypes.instanceOf(Date),
};

export default SessionCallMessage;
